import { Injectable } from '@angular/core';
import { Area } from '../../shared/enums/area.enum';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  /**
   * Deserialize the given json and return it. If the JSON parsing fails a friendly message is output to the console
   * @param json the json to deserialize
   * @return undefined if a serialization error occured or the deserialized json
   */
  public static deserializeJson<T = unknown>(json: string | null): T | undefined {
    try {
      if (!json) {
        return undefined;
      }

      // 'null' is correctly handled by JSON.parse because null is part of the Json spec while undefined is not
      // cf. https://stackoverflow.com/questions/17648150/how-does-json-parse-manage-undefined

      const obj = JSON.parse(json);
      return obj as T;
    } catch (error) {
      console.warn('Unable to deserialize obj json. \n', json, '\n', error);
      return undefined;
    }
  }

  /**
   * Copy properties from the given json or from the session storage to dest.
   * If json is not null the copy is tried from the json and the result is stored to the session storage using the given key.
   * If json is null the copy is tried from the session storage using the given key.
   * @param dest the destination object
   * @param json a JSON of the same type as the destinationObj (may be null)
   * @param key
   * @return true if an object has been read from the session storage, else false.
   */
  public CopyPropertiesFromJsonAndWriteOrRead<T>(dest: T, json: string | null | undefined, key: string): boolean {
    if (json) {
      const jsonObj = SessionStorageService.deserializeJson<Record<string, unknown>>(json);
      SessionStorageService.copy(dest, jsonObj);
      this.write(key, dest);
    } else {
      const objFromSessionStorage = this.read<Record<string, unknown>>(key);
      if (objFromSessionStorage) {
        SessionStorageService.copy(dest, objFromSessionStorage);
        return true;
      }
    }

    return false;
  }

  public read<T = unknown>(key: string): T | null {
    const json = sessionStorage.getItem(key);
    return SessionStorageService.deserializeJson(json) as T | null;
  }

  public removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  public tryGetArea(key: string): Area | undefined {
    let area: Area | undefined;
    const strArea = this.read<string>(key);
    if (strArea) {
      area = JSON.parse(strArea);
    }
    return area;
  }

  /**
   * Serialize an object to a json string and write it to the session storage
   * @param obj the obj to store to the session storage
   * @param key the key where the obj is stored to the session storage
   */
  public write(key: string, obj: unknown): void {
    try {
      const strValue = JSON.stringify(obj);
      sessionStorage.setItem(key, strValue);
    } catch (error) {
      console.error(`Unable to register obj. key = ${key} \n`, error);
    }
  }

  /**
   * Copy an object to another object
   * @param dest the destination object
   * @param src the source object
   */
  private static copy<T>(dest: T, src: Record<string, unknown> | undefined): void {
    for (const propName in src) {
      const hasPropertyFlag = Object.prototype.hasOwnProperty.call(src, propName);
      if (hasPropertyFlag) {
        try {
          (dest as Record<string, unknown>)[propName] = src[propName];
        } catch (error) {
          console.warn(error);
        }
      }
    }
  }
}
